import { Injectable, signal, computed, effect } from '@angular/core';
import { updatePreset } from '@primeuix/themes';
import { LocalStorageService } from '../storage/local-storage.service';

export interface ThemeConfig {
  name: string;
  displayName: string;
  isDark: boolean;
  semanticColors: {
    primary: Record<string, string>;
    secondary: Record<string, string>;
    tertiary?: Record<string, string>;
    accent?: Record<string, string>;
  };
  fontFamily?: string;
  darkModeColors?: {
    primary: Record<string, string>;
    secondary: Record<string, string>;
    tertiary?: Record<string, string>;
    accent?: Record<string, string>;
  };
}

export interface ThemeState {
  currentTheme: string;
  isDarkMode: boolean;
  fontFamily: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_STORAGE_KEY = 'app-theme';
  private readonly DARK_MODE_STORAGE_KEY = 'app-dark-mode';
  private readonly FONT_STORAGE_KEY = 'app-font-family';
  private readonly THEME_VERSION_KEY = 'app-theme-version';
  private readonly CURRENT_THEME_VERSION = '1.0.0';

  // Signals for reactive theme state
  private _currentTheme = signal<string>('light');
  private _isDarkMode = signal<boolean>(false);
  private _fontFamily = signal<string>('Public Sans');

  // Computed values
  public currentTheme = computed(() => this._currentTheme());
  public isDarkMode = computed(() => this._isDarkMode());
  public fontFamily = computed(() => this._fontFamily());
  public themeState = computed(() => ({
    currentTheme: this._currentTheme(),
    isDarkMode: this._isDarkMode(),
    fontFamily: this._fontFamily()
  }));

  // Available themes configuration
  private readonly themes: Record<string, ThemeConfig> = {
    light: {
      name: 'light',
      displayName: 'Light',
      isDark: false,
      semanticColors: {
        primary: {
          50: '{mcx.gray.50}',
          100: '{mcx.gray.100}',
          200: '{mcx.gray.200}',
          300: '{mcx.gray.300}',
          400: '{mcx.gray.400}',
          500: '{mcx.gray.500}',
          600: '{mcx.gray.600}',
          700: '{mcx.gray.700}',
          800: '{mcx.gray.800}',
          900: '{mcx.gray.900}',
          950: '{mcx.gray.950}'
        },
        secondary: {
          50: '{mcx.blue.50}',
          100: '{mcx.blue.100}',
          200: '{mcx.blue.200}',
          300: '{mcx.blue.300}',
          400: '{mcx.blue.400}',
          500: '{mcx.blue.500}',
          600: '{mcx.blue.600}',
          700: '{mcx.blue.700}',
          800: '{mcx.blue.800}',
          900: '{mcx.blue.900}',
          950: '{mcx.blue.950}'
        },
        tertiary: {
          50: '{mcx.red.50}',
          100: '{mcx.red.100}',
          200: '{mcx.red.200}',
          300: '{mcx.red.300}',
          400: '{mcx.red.400}',
          500: '{mcx.red.500}',
          600: '{mcx.red.600}',
          700: '{mcx.red.700}',
          800: '{mcx.red.800}',
          900: '{mcx.red.900}',
          950: '{mcx.red.950}'
        },
        accent: {
          50: '{mcx.gold.50}',
          100: '{mcx.gold.100}',
          200: '{mcx.gold.200}',
          300: '{mcx.gold.300}',
          400: '{mcx.gold.400}',
          500: '{mcx.gold.500}',
          600: '{mcx.gold.600}',
          700: '{mcx.gold.700}',
          800: '{mcx.gold.800}',
          900: '{mcx.gold.900}',
          950: '{mcx.gold.950}'
        }
      },
      darkModeColors: {
        primary: {
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cbd5e1',
          400: '#94a3b8',
          500: '#64748b',
          600: '#475569',
          700: '#334155',
          800: '#1e293b',
          900: '#0f172a',
          950: '#020617'
        },
        secondary: {
          50: '#eff6ff',
          100: '#dbeafe',
          200: '#bfdbfe',
          300: '#93c5fd',
          400: '#60a5fa',
          500: '#3b82f6',
          600: '#2563eb',
          700: '#1d4ed8',
          800: '#1e40af',
          900: '#1e3a8a',
          950: '#172554'
        },
        tertiary: {
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
          950: '#450a0a'
        },
        accent: {
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
          950: '#451a03'
        }
      },
      fontFamily: 'Public Sans'
    },
    dark: {
      name: 'dark',
      displayName: 'Dark',
      isDark: true,
      semanticColors: {
        primary: {
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cbd5e1',
          400: '#94a3b8',
          500: '#64748b',
          600: '#475569',
          700: '#334155',
          800: '#1e293b',
          900: '#0f172a',
          950: '#020617'
        },
        secondary: {
          50: '#eff6ff',
          100: '#dbeafe',
          200: '#bfdbfe',
          300: '#93c5fd',
          400: '#60a5fa',
          500: '#3b82f6',
          600: '#2563eb',
          700: '#1d4ed8',
          800: '#1e40af',
          900: '#1e3a8a',
          950: '#172554'
        },
        tertiary: {
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
          950: '#450a0a'
        },
        accent: {
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
          950: '#451a03'
        }
      },
      darkModeColors: {
        primary: {
          50: '#ffffff',
          100: '#1e293b',
          200:  '#f1f5f9',
          300: '#e2e8f0',
          400: '#cbd5e1',
          500: '#94a3b8',
          600: '#64748b',
          700: '#475569',
          800: '#334155',
          900:'#243b53',
          950: '#0f172a',
        },
        secondary: {
          50: '#eff6ff',
          100: '#dbeafe',
          200: '#bfdbfe',
          300: '#93c5fd',
          400: '#60a5fa',
          500: '#3b82f6',
          600: '#2563eb',
          700: '#1d4ed8',
          800: '#1e40af',
          900: '#1e3a8a',
          950: '#172554',
        },
        tertiary: {
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
          950: '#450a0a',
        },
        accent: {
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
          950: '#451a03',
        }
      },
      fontFamily: 'Public Sans'
    },
    teal: {
      name: 'teal',
      displayName: 'Teal',
      isDark: false,
      semanticColors: {
        primary: {
          50: '{ppm.teal.50}',
          100: '{ppm.teal.100}',
          200: '{ppm.teal.200}',
          300: '{ppm.teal.300}',
          400: '{ppm.teal.400}',
          500: '{ppm.teal.500}',
          600: '{ppm.teal.600}',
          700: '{ppm.teal.700}',
          800: '{ppm.teal.800}',
          900: '{ppm.teal.900}',
          950: '{ppm.teal.950}'
        },
        secondary: {
          50: '{mcx.blue.50}',
          100: '{mcx.blue.100}',
          200: '{mcx.blue.200}',
          300: '{mcx.blue.300}',
          400: '{mcx.blue.400}',
          500: '{mcx.blue.500}',
          600: '{mcx.blue.600}',
          700: '{mcx.blue.700}',
          800: '{mcx.blue.800}',
          900: '{mcx.blue.900}',
          950: '{mcx.blue.950}'
        }
      },
      darkModeColors: {
        primary: {
          50: '{ppm.teal.50}',
          100: '{ppm.teal.100}',
          200: '{ppm.teal.200}',
          300: '{ppm.teal.300}',
          400: '{ppm.teal.400}',
          500: '{ppm.teal.500}',
          600: '{ppm.teal.600}',
          700: '{ppm.teal.700}',
          800: '{ppm.teal.800}',
          900: '{ppm.teal.900}',
          950: '{ppm.teal.950}'
        },
        secondary: {
          50: '#eff6ff',
          100: '#dbeafe',
          200: '#bfdbfe',
          300: '#93c5fd',
          400: '#60a5fa',
          500: '#3b82f6',
          600: '#2563eb',
          700: '#1d4ed8',
          800: '#1e40af',
          900: '#1e3a8a',
          950: '#172554',
        },
        tertiary: {
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
          950: '#450a0a',
        },
        accent: {
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
          950: '#451a03',
        }
      },
      fontFamily: 'Public Sans'
    },
    blue: {
      name: 'blue',
      displayName: 'Blue',
      isDark: false,
      semanticColors: {
        primary: {
          50: '#f0f4f8',
          100: '#d9e2ec',
          200: '#bcccdc',
          300: '#9fb3c8',
          400: '#829ab1',
          500: '#627d98',
          600: '#486581',
          700: '#334e68',
          800: '#243b53',
          900: '#102a43',
          950: '#0a1929'
        },
        secondary: {
          50: '#f1f5f9',
          100: '#e2e8f0',
          200: '#cbd5e1',
          300: '#94a3b8',
          400: '#64748b',
          500: '#475569',
          600: '#334155',
          700: '#1e293b',
          800: '#0f172a',
          900: '#020617',
          950: '#000000'
        },
        tertiary: {
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
          950: '#450a0a'
        },
        accent: {
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
          950: '#451a03'
        }
      },
      darkModeColors: {
        primary: {
          50: '#f0f4f8',
          100: '#d9e2ec',
          200: '#bcccdc',
          300: '#9fb3c8',
          400: '#829ab1',
          500: '#627d98',
          600: '#486581',
          700: '#334e68',
          800: '#243b53',
          900: '#102a43',
          950: '#0a1929'
        },
        secondary: {
          50: '#f1f5f9',
          100: '#e2e8f0',
          200: '#cbd5e1',
          300: '#94a3b8',
          400: '#64748b',
          500: '#475569',
          600: '#334155',
          700: '#1e293b',
          800: '#0f172a',
          900: '#020617',
          950: '#000000'
        },
        tertiary: {
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
          950: '#450a0a'
        },
        accent: {
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
          950: '#451a03'
        }
      },
      fontFamily: 'Public Sans'
    },
    purple: {
      name: 'purple',
      displayName: 'Purple',
      isDark: false,
      semanticColors: {
        primary: {
          50: '#f3f0ff',
          100: '#e9e0ff',
          200: '#d6c7ff',
          300: '#b894ff',
          400: '#9f5bff',
          500: '#8b3dff',
          600: '#7c2dff',
          700: '#6b1dff',
          800: '#5a0dff',
          900: '#4a00e5',
          950: '#3b0764'
        },
        secondary: {
          50: '#f1f5f9',
          100: '#e2e8f0',
          200: '#cbd5e1',
          300: '#94a3b8',
          400: '#64748b',
          500: '#475569',
          600: '#334155',
          700: '#1e293b',
          800: '#0f172a',
          900: '#020617',
          950: '#000000'
        },
        tertiary: {
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
          950: '#450a0a'
        },
        accent: {
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
          950: '#451a03'
        }
      },
      darkModeColors: {
        primary: {
          50: '#f3f0ff',
          100: '#e9e0ff',
          200: '#d6c7ff',
          300: '#b894ff',
          400: '#9f5bff',
          500: '#8b3dff',
          600: '#7c2dff',
          700: '#6b1dff',
          800: '#5a0dff',
          900: '#4a00e5',
          950: '#3b0764'
        },
        secondary: {
          50: '#f1f5f9',
          100: '#e2e8f0',
          200: '#cbd5e1',
          300: '#94a3b8',
          400: '#64748b',
          500: '#475569',
          600: '#334155',
          700: '#1e293b',
          800: '#0f172a',
          900: '#020617',
          950: '#000000'
        },
        tertiary: {
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
          950: '#450a0a'
        },
        accent: {
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
          950: '#451a03'
        }
      },
      fontFamily: 'Public Sans'
    }
  };

  // Available fonts
  public readonly availableFonts = [
    'Public Sans',
    'IBM Plex Sans',
    'Roboto',
    'Inter',
    'Open Sans'
  ];

  constructor(private localStorage: LocalStorageService) {
    // Initialize theme from storage
    this.initializeTheme();
    
    // Apply theme immediately after initialization
    setTimeout(() => {
      const initialThemeState = this.themeState();
      this.applyTheme(initialThemeState);
    }, 0);
    
    // Effect to apply theme changes for future updates
    effect(() => {
      const themeState = this.themeState();
      this.applyTheme(themeState);
    });
  }

  /**
   * Initialize theme from localStorage or use defaults
   */
  private initializeTheme(): void {
    try {
      // Check theme version for potential migrations
      const storedVersion = this.localStorage.getItem(this.THEME_VERSION_KEY);
      if (storedVersion !== this.CURRENT_THEME_VERSION) {
        this.migrateThemeStorage();
      }

      // Load theme preferences
      const storedTheme = this.localStorage.getItem(this.THEME_STORAGE_KEY);
      const storedDarkMode = this.localStorage.getItem(this.DARK_MODE_STORAGE_KEY);
      const storedFont = this.localStorage.getItem(this.FONT_STORAGE_KEY);

      // Set theme with validation
      if (storedTheme && this.themes[storedTheme]) {
        this._currentTheme.set(storedTheme);
      } else {
        this._currentTheme.set('light');
      }

      // Set dark mode
      if (storedDarkMode !== null) {
        this._isDarkMode.set(storedDarkMode === 'true');
      } else {
        this._isDarkMode.set(false);
      }

      // Set font family
      if (storedFont && this.availableFonts.includes(storedFont)) {
        this._fontFamily.set(storedFont);
      } else {
        this._fontFamily.set('Public Sans');
      }

      // Save current version
      this.localStorage.setItem(this.THEME_VERSION_KEY, this.CURRENT_THEME_VERSION);

    } catch (error) {
      console.warn('Failed to initialize theme from storage:', error);
      // Use defaults
      this._currentTheme.set('light');
      this._isDarkMode.set(false);
      this._fontFamily.set('Public Sans');
    }
  }

  /**
   * Migrate theme storage if needed
   */
  private migrateThemeStorage(): void {
    // Add migration logic here if needed in the future
  }

  /**
   * Apply theme configuration to PrimeNG
   */
  private applyTheme(themeState: ThemeState): void {
    try {
      const theme = this.themes[themeState.currentTheme];
      if (!theme) {
        console.warn(`Theme '${themeState.currentTheme}' not found, using light theme`);
        this._currentTheme.set('light');
        return;
      }

      // Choose colors based on dark mode setting
      const colors = themeState.isDarkMode && theme.darkModeColors 
        ? theme.darkModeColors 
        : theme.semanticColors;

      // Apply semantic colors
      updatePreset({
        semantic: {
          primary: colors.primary,
          secondary: colors.secondary,
          ...(colors.tertiary && { tertiary: colors.tertiary }),
          ...(colors.accent && { accent: colors.accent }),
          font: {
            family: themeState.fontFamily
          }
        }
      });

      // Ensure non-PrimeNG text updates immediately too.
      document.documentElement.style.setProperty('--p-font-family', `"${themeState.fontFamily}", sans-serif`);
      document.documentElement.style.fontFamily = `"${themeState.fontFamily}", sans-serif`;

      // Apply dark mode to document
      this.applyDarkMode(themeState.isDarkMode);

      // Apply data-theme attribute for CSS custom properties
      this.applyDataTheme(themeState.currentTheme);

    } catch (error) {
      console.error('Failed to apply theme:', error);
    }
  }

  /**
   * Apply dark mode to document
   */
  private applyDarkMode(isDark: boolean): void {
    const htmlElement = document.documentElement;
    if (isDark) {
      htmlElement.classList.add('dark-mode');
    } else {
      htmlElement.classList.remove('dark-mode');
    }
  }

  /**
   * Apply data-theme attribute for CSS custom properties
   */
  private applyDataTheme(themeName: string): void {
    const bodyElement = document.body;
    bodyElement.setAttribute('data-theme', themeName);
  }

  /**
   * Set the current theme
   */
  public setTheme(themeName: string): void {
    if (!this.themes[themeName]) {
      console.warn(`Theme '${themeName}' not found`);
      return;
    }

    this._currentTheme.set(themeName);
    this.localStorage.setItem(this.THEME_STORAGE_KEY, themeName);
  }

  /**
   * Toggle dark mode
   */
  public toggleDarkMode(): void {
    const newDarkMode = !this._isDarkMode();
    this._isDarkMode.set(newDarkMode);
    this.localStorage.setItem(this.DARK_MODE_STORAGE_KEY, newDarkMode.toString());
  }

  /**
   * Set dark mode
   */
  public setDarkMode(isDark: boolean): void {
    this._isDarkMode.set(isDark);
    this.localStorage.setItem(this.DARK_MODE_STORAGE_KEY, isDark.toString());
  }

  /**
   * Set font family
   */
  public setFontFamily(fontFamily: string): void {
    if (!this.availableFonts.includes(fontFamily)) {
      console.warn(`Font '${fontFamily}' not available`);
      return;
    }

    this._fontFamily.set(fontFamily);
    this.localStorage.setItem(this.FONT_STORAGE_KEY, fontFamily);
  }

  /**
   * Get available themes
   */
  public getAvailableThemes(): ThemeConfig[] {
    return Object.values(this.themes);
  }

  /**
   * Get theme configuration by name
   */
  public getTheme(themeName: string): ThemeConfig | undefined {
    return this.themes[themeName];
  }

  /**
   * Reset theme to defaults
   */
  public resetTheme(): void {
    this._currentTheme.set('light');
    this._isDarkMode.set(false);
    this._fontFamily.set('Public Sans');
    
    this.localStorage.setItem(this.THEME_STORAGE_KEY, 'light');
    this.localStorage.setItem(this.DARK_MODE_STORAGE_KEY, 'false');
    this.localStorage.setItem(this.FONT_STORAGE_KEY, 'Public Sans');
  }

  /**
   * Check if a theme exists
   */
  public hasTheme(themeName: string): boolean {
    return themeName in this.themes;
  }
}
